import { CRM } from "@/components/atomic-crm/root/CRM";
import { useEffect, useState } from "react";

/**
 * Application entry point
 *
 * Customize Atomic CRM by passing props to the CRM component:
 *  - companySectors
 *  - darkTheme
 *  - dealCategories
 *  - dealPipelineStatuses
 *  - dealStages
 *  - lightTheme
 *  - logo
 *  - noteStatuses
 *  - taskTypes
 *  - title
 * ... as well as all the props accepted by shadcn-admin-kit's <Admin> component.
 *
 * @example
 * const App = () => (
 *    <CRM
 *       logo="./img/logo.png"
 *       title="Acme CRM"
 *    />
 * );
 */
type BackendState = "checking" | "available" | "unavailable";

const App = () => {
  const [backendState, setBackendState] =
    useState<BackendState>("checking");

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/`, {
      headers: {
        apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
      },
      signal: controller.signal,
    })
      .then(() => setBackendState("available"))
      .catch(() => setBackendState("unavailable"))
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, []);

  if (backendState === "checking") {
    return (
      <div className="crm-status-screen">
        <div className="crm-status-card">
          <p className="crm-status-kicker">MightyBot CRM</p>
          <h1>Connecting to the CRM backend</h1>
          <p>Checking the database connection before loading the workspace.</p>
        </div>
      </div>
    );
  }

  if (backendState === "unavailable") {
    return (
      <div className="crm-status-screen">
        <div className="crm-status-card crm-status-card-error">
          <p className="crm-status-kicker">MightyBot CRM</p>
          <h1>CRM backend is offline</h1>
          <p>
            The CRM database project is not reachable right now. This usually
            means the Supabase project is paused or inactive and must be
            restored before the CRM can load.
          </p>
          <div className="crm-status-actions">
            <a href="https://supabase.com/dashboard/project/orjmqbceblvzqhjfagch">
              Open Supabase project
            </a>
            <button type="button" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <CRM />;
};

export default App;
